import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Label } from '../../lib/shadcn/label'
import { Input } from '../../lib/shadcn/input'
import { Printer } from 'lucide-react'
import { usePostProductLabels } from '../../hooks/backend/inventoryPage'
import { toast } from '../../lib/shadcn/sonner'
import type { Product } from './ProductTable'

interface PrintLabelsDialogProps {
  product: Product | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function PrintLabelsDialog({
  product,
  open,
  onOpenChange,
}: PrintLabelsDialogProps) {
  const postProductLabels = usePostProductLabels()

  const [front, setFront] = useState(true)
  const [back, setBack] = useState(true)
  const [copies, setCopies] = useState('1')

  // Reset whenever a new product/dialog opens
  useEffect(() => {
    if (open) {
      setFront(true)
      setBack(true)
      setCopies('1')
    }
  }, [open])

  const copiesNum = Math.max(1, Math.round(Number(copies) || 1))
  const canPrint = (front || back) && !!product

  const handlePrint = async () => {
    if (!product) return
    try {
      await postProductLabels.trigger({
        product: {
          sku: product.sku,
          item_name: product.item_name,
          label_name: product.label_name,
          weight_oz: product.weight_oz,
          description: product.description,
          ingredients: product.ingredients,
          allergens: product.allergens,
        },
        front,
        back,
        copies: copiesNum,
      })
      const sides = [front && 'Front', back && 'Back'].filter(Boolean).join(' & ')
      toast.success(
        `Printing ${sides} label${copiesNum !== 1 ? 's' : ''} (${copiesNum} cop${copiesNum !== 1 ? 'ies' : 'y'})`,
      )
      onOpenChange(false)
    } catch {
      toast.error('Failed to print labels. Please try again.')
    }
  }

  const productLabel = product
    ? `${product.item_name ?? product.sku} — ${product.sku}`
    : ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Print Labels</DialogTitle>
          <DialogDescription>
            {product ? productLabel : 'Select which labels to print.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-3">
            <Label className="text-sm font-medium text-muted-foreground">
              Label sides
            </Label>
            <div className="flex items-center gap-2">
              <Checkbox
                id="label-front"
                checked={front}
                onCheckedChange={v => setFront(v === true)}
              />
              <Label htmlFor="label-front" className="cursor-pointer font-normal">
                Front
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="label-back"
                checked={back}
                onCheckedChange={v => setBack(v === true)}
              />
              <Label htmlFor="label-back" className="cursor-pointer font-normal">
                Back
              </Label>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="label-copies" className="text-sm font-medium text-muted-foreground">
              Number of copies
            </Label>
            <Input
              id="label-copies"
              type="number"
              min={1}
              value={copies}
              onChange={e => setCopies(e.target.value)}
              className="w-28"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void handlePrint()}
            disabled={!canPrint || postProductLabels.loading}
            className="gap-1.5"
          >
            <Printer className="w-4 h-4" />
            {postProductLabels.loading ? 'Printing…' : 'Print'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
